import React, { FunctionComponent, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ButtonProgress from '../ButtonProgress/ButtonProgress';
import Auth from '@aws-amplify/auth';

const useStyles = makeStyles((theme: Theme) => ({
  mainDialog: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: theme.spacing(50),
      width: theme.spacing(50),
    },
  },
  dialogContent: {
    '& > *': {
      marginTop: theme.spacing(2),
    },
  },
  actions: {},
  formSubmit: {
    marginTop: theme.spacing(-3),
  },
}));

export const useChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const changePassword = async (oldPassword: string, newPassword: string) => {
    setIsLoading(true);
    try {
      const me = await Auth.currentAuthenticatedUser();
      const result = await Auth.changePassword(me, oldPassword, newPassword);
      return { success: result === 'SUCCESS' };
    } catch (err: any) {
      const friendlyError =
        err.code === 'NotAuthorizedException'
          ? new Error('Incorrect password')
          : err;
      return { success: false, error: friendlyError };
    } finally {
      setIsLoading(false);
    }
  };
  return { isLoading, changePassword };
};

interface Props {
  isOpen?: boolean;
  onClose(): void;
  onConfirm(isSuccess: boolean, hasError: boolean): void;
  onNotification(msg: string, variant: string): void;
  updateUserFlag(): Promise<Response>;
  handleLogout(): void;
  needResetPassword: boolean;
}

const ChangePasswordDialog: FunctionComponent<Props> = ({
  isOpen = true,
  needResetPassword,
  onClose,
  onConfirm,
  onNotification,
  updateUserFlag,
  handleLogout,
}) => {
  const classes = useStyles();

  const { changePassword, isLoading } = useChangePassword();

  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [newPwdConfirm, setNewPwdConfirm] = useState('');

  const validateInputs = () => {
    if (oldPwd.length < 8 || newPwd.length < 14) {
      onNotification('Minimum password length is 14 characters', 'warning');
      return false;
    }
    if (newPwd.length > 256) {
      onNotification('Maximum password length is 256 characters', 'warning');
      return false;
    }
    if (newPwd !== newPwdConfirm) {
      onNotification('New password does not match', 'warning');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInputs()) return;

    const { success, error } = await changePassword(oldPwd, newPwd);
    const isSuccess = Boolean(success);

    if (!success) {
      onNotification(
        error?.message || 'Unknown error. Please try again later.',
        'error'
      );
      return;
    }

    if (needResetPassword) {
      try {
        await updateUserFlag();
        onConfirm(isSuccess, false);
      } catch (err) {
        window.location.reload();
      }
    } else {
      onConfirm(isSuccess, false);
    }
  };

  const handleClose = () => {
    if (!needResetPassword) {
      onClose();
    }
  };

  const requirePasswordReset = Boolean(needResetPassword);
  return (
    <Dialog
      className={classes.mainDialog}
      aria-labelledby="dialog-title"
      open={isOpen || requirePasswordReset}
      onClose={handleClose}
    >
      <DialogTitle id="dialog-title">Change Password</DialogTitle>
      <form className={classes.formSubmit} onSubmit={handleSubmit}>
        <DialogContent className={classes.dialogContent}>
          {requirePasswordReset && (
            <Typography color="primary" variant="subtitle2">
              <Warning fontSize="small" style={{ verticalAlign: 'middle' }} />{' '}
              Password requirement change. Please change your password.
            </Typography>
          )}
          <TextField
            margin="dense"
            variant="outlined"
            fullWidth
            type="password"
            required
            label="Current password"
            value={oldPwd}
            onChange={(e) => setOldPwd(e.target.value)}
          />
          <TextField
            margin="dense"
            variant="outlined"
            fullWidth
            type="password"
            required
            label="New password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
          />
          <TextField
            margin="dense"
            variant="outlined"
            fullWidth
            type="password"
            required
            label="Confirm new password"
            value={newPwdConfirm}
            onChange={(e) => setNewPwdConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions classes={{ root: classes.actions }}>
          {!requirePasswordReset && <Button onClick={onClose}>Cancel</Button>}
          {requirePasswordReset && (
            <Button onClick={handleLogout}>Logout</Button>
          )}
          <ButtonProgress
            isLoading={isLoading}
            type="submit"
            variant="contained"
            color="primary"
          >
            Confirm
          </ButtonProgress>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ChangePasswordDialog;
